import React from "react";
import { StyleSheet, View, Text, TouchableOpacity } from "react-native";
import { AppVersion } from "@/versions";
import type { IFeedbackDiagnostics } from "@/services/feedback-metadata";

export interface IFeedbackDiagnosticsBoxProps {
  currentScreenUrl: string;
  diagnostics: IFeedbackDiagnostics;
  showDiagnosticsDetails: boolean;
  onToggleDetails: () => void;
  inputReadOnlyBg: string;
  inputReadOnlyBorder: string;
  inputReadOnlyText: string;
  tintColor: string;
}

/**
 * Collapsible environment & hardware diagnostics panel for the FeedbackModal.
 */
export function FeedbackDiagnosticsBox({
  currentScreenUrl,
  diagnostics,
  showDiagnosticsDetails,
  onToggleDetails,
  inputReadOnlyBg,
  inputReadOnlyBorder,
  inputReadOnlyText,
  tintColor,
}: IFeedbackDiagnosticsBoxProps) {
  return (
    <View
      style={[
        styles.contextBox,
        { borderColor: inputReadOnlyBorder, backgroundColor: inputReadOnlyBg },
      ]}
    >
      <View style={styles.contextRow}>
        <Text style={[styles.contextLabel, { color: inputReadOnlyText }]}>Screen:</Text>
        <Text style={[styles.contextValue, { color: inputReadOnlyText }]} numberOfLines={1}>
          {currentScreenUrl}
        </Text>
      </View>
      <View style={styles.contextRow}>
        <Text style={[styles.contextLabel, { color: inputReadOnlyText }]}>Env:</Text>
        <Text style={[styles.contextValue, { color: inputReadOnlyText }]} numberOfLines={1}>
          v{AppVersion} • {diagnostics.device.modelName} ({diagnostics.system.platform}{" "}
          {diagnostics.system.osVersion})
        </Text>
      </View>

      <TouchableOpacity
        onPress={onToggleDetails}
        style={styles.detailsToggleRow}
        accessibilityRole="button"
        accessibilityLabel="Toggle environment diagnostics preview"
      >
        <Text style={[styles.detailsToggleText, { color: tintColor }]}>
          {showDiagnosticsDetails
            ? "Hide Environment Diagnostics ▴"
            : "View Environment Diagnostics ▾"}
        </Text>
      </TouchableOpacity>

      {showDiagnosticsDetails && (
        <View style={styles.detailsGrid}>
          <Text style={[styles.detailItemText, { color: inputReadOnlyText }]}>
            • Hardware: {diagnostics.device.brand} {diagnostics.device.modelName} (
            {diagnostics.device.deviceType})
          </Text>
          <Text style={[styles.detailItemText, { color: inputReadOnlyText }]}>
            • Display: {diagnostics.display.windowResolution} ({diagnostics.display.pixelRatio},{" "}
            {diagnostics.display.orientation})
          </Text>
          <Text style={[styles.detailItemText, { color: inputReadOnlyText }]}>
            • Memory & CPU: {diagnostics.device.memory} RAM • {diagnostics.device.cpuCores}
          </Text>
          <Text style={[styles.detailItemText, { color: inputReadOnlyText }]}>
            • Localization: {diagnostics.locale.locale} ({diagnostics.locale.timeZone})
          </Text>
          <Text style={[styles.detailItemText, { color: inputReadOnlyText }]}>
            • Network: {diagnostics.locale.networkStatus} ({diagnostics.locale.connectionType})
          </Text>
          <Text
            style={[
              styles.detailItemText,
              { color: inputReadOnlyText, fontStyle: "italic", marginTop: 2 },
            ]}
          >
            * Personal user details (ID/email/name) are strictly omitted for privacy.
          </Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  contextBox: {
    padding: 10,
    borderWidth: 1,
    borderRadius: 0,
    marginBottom: 16,
  },
  contextRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  contextLabel: {
    fontSize: 12,
    fontWeight: "600",
    width: 60,
  },
  contextValue: {
    fontSize: 12,
    flex: 1,
  },
  detailsToggleRow: {
    paddingVertical: 4,
    marginTop: 4,
  },
  detailsToggleText: {
    fontSize: 11,
    fontWeight: "600",
    textDecorationLine: "underline",
  },
  detailsGrid: {
    marginTop: 6,
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: "rgba(128, 128, 128, 0.2)",
    gap: 3,
  },
  detailItemText: {
    fontSize: 11,
    lineHeight: 15,
  },
});
